
import { useState } from 'react';
import { teamMembers as initialTeam } from '../../../mocks/dashboardData';
import { createTeam, deleteTeam, getTeams, updateTeam } from '../../../api/apiServices';

export default function TeamSection() {
  const [team, setTeam] = useState(initialTeam);
  const [editingMember, setEditingMember] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  const emptyMember = {
    id: Date.now(),
    name: '',
    role: '',
    image: '',
    bio: '',
    isPublished: true
  };

  /* ---------------- LOAD ---------------- */
    useEffect(() => {
      fetchTeams();
    }, []);
  
    const fetchTeams = async () => {
      try {
        setLoading(true);
        const res = await getTeams();
        setTeam(res.data || []);
      } catch (err) {
        console.error("Failed to load teams", err);
      } finally {
        setLoading(false);
      }
    };
  
    /* ---------------- IMAGE HANDLER ---------------- */
    const handleImageChange = (file) => {
      if (!file) return;
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    };

  const handleSave = async () => {
      try {
        const formData = new FormData();
  
        formData.append("name", editingMember.name);
        formData.append("role", editingMember.role);
        formData.append("bio", editingMember.bio);
        formData.append("isPublished", editingMember.isPublished);
  
        if (imageFile) {
          formData.append("image", imageFile);
        }
  
        if (isAdding) {
          await createTeam(formData);
        } else {
          await updateTeam(editingMember._id, formData);
        }
  
        await fetchTeams();
  
        setEditingMember(null);
        setIsAdding(false);
        setImageFile(null);
        setImagePreview("");
  
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } catch (err) {
        console.error("Save failed", err);
      }
    };

   /* ---------------- DELETE ---------------- */
    const handleDelete = async (id) => {
      if (!confirm("Are you sure you want to delete this team member?")) return;
  
      try {
        await deleteTeam(id);
        setTeam(team.filter((t) => t._id !== id));
      } catch (err) {
        console.error("Delete failed", err);
      }
    };
  
    /* ---------------- TOGGLE PUBLISH ---------------- */
    const togglePublish = async (team) => {
      try {
        const formData = new FormData();
        formData.append("isPublished", !team.isPublished);
  
        await updateTeam(team._id, formData);
  
        setTeam((prev) =>
          prev.map((t) =>
            t._id === team._id
              ? { ...t, isPublished: !t.isPublished }
              : t
          )
        );
      } catch (err) {
        console.error("Publish toggle failed", err);
      }
    };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Team Members</h2>
          <p className="text-gray-600 text-sm">Manage your organization's team</p>
        </div>
        <button
          onClick={() => { setEditingMember(emptyMember); setIsAdding(true); }}
          className="bg-[#6a4bc3] text-white px-6 py-2.5 rounded-lg font-medium hover:bg-[#5a3ba3] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-add-line"></i>
          Add Member
        </button>
      </div>

      {showSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
          <i className="ri-check-line"></i>
          Team member saved successfully!
        </div>
      )}

      {/* Team Grid */}
      {!editingMember && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {team.map((member) => (
            <div key={member.id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
              <div className="w-full h-48 overflow-hidden">
                <img src={member.image} alt={member.name} className="w-full h-full object-cover object-top" />
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">{member.name}</h3>
                    <p className="text-xs text-[#6a4bc3]">{member.role}</p>
                  </div>
                  <button
                    onClick={() => togglePublish(member.id)}
                    className={`w-2 h-2 rounded-full cursor-pointer ${member.isPublished ? 'bg-green-500' : 'bg-gray-400'}`}
                    title={member.isPublished ? 'Published' : 'Draft'}
                  />
                </div>
                <p className="text-xs text-gray-600 line-clamp-2 mb-3">{member.bio}</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setEditingMember(member); setIsAdding(false); }}
                    className="flex-1 py-2 text-xs font-medium text-[#6a4bc3] bg-[#6a4bc3]/10 rounded-lg hover:bg-[#6a4bc3]/20 transition-colors cursor-pointer"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(member.id)}
                    className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit/Add Form */}
      {editingMember && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">{isAdding ? 'Add New Team Member' : 'Edit Team Member'}</h3>
            <button
              onClick={() => { setEditingMember(null); setIsAdding(false); }}
              className="text-gray-500 hover:text-gray-700 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          <div className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <input
                  type="text"
                  value={editingMember.name}
                  onChange={(e) => setEditingMember({...editingMember, name: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6a4bc3]"
                  placeholder="e.g., Dr. Sarah Mitchell"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role/Position</label>
                <input
                  type="text"
                  value={editingMember.role}
                  onChange={(e) => setEditingMember({...editingMember, role: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6a4bc3]"
                  placeholder="e.g., Executive Director"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Photo</label>
              {/* IMAGE */}
          <div className="space-y-2">
            {imagePreview && (
              <img
                src={imagePreview}
                className="w-32 h-32 rounded-full object-cover border"
                alt="Preview"
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageChange(e.target.files[0])}
            />
          </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
              <textarea
                value={editingMember.bio}
                onChange={(e) => setEditingMember({...editingMember, bio: e.target.value})}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6a4bc3] resize-none"
                placeholder="Brief biography"
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="member-published"
                checked={editingMember.isPublished}
                onChange={(e) => setEditingMember({...editingMember, isPublished: e.target.checked})}
                className="w-4 h-4 text-[#6a4bc3] rounded cursor-pointer"
              />
              <label htmlFor="member-published" className="text-sm text-gray-700 cursor-pointer">Show on website</label>
            </div>

            <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={handleSave}
                className="bg-[#6a4bc3] text-white px-6 py-2.5 rounded-lg font-medium hover:bg-[#5a3ba3] transition-colors cursor-pointer whitespace-nowrap"
              >
                {isAdding ? 'Add Member' : 'Save Changes'}
              </button>
              <button
                onClick={() => { setEditingMember(null); setIsAdding(false); }}
                className="px-6 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
